import React, { useEffect, useRef } from "react";
import MagneticButton from "../components/MagneticButton";
import ParticleHero from "../components/ParticleHero";
import { ChevronDown, ArrowDown } from "lucide-react";

const TICKER_ITEMS = [
  "AI Strategy",
  "Performance Marketing",
  "Web & Mobile Builds",
  "Workflow Automation",
  "Managed IT",
  "Go-To-Market",
  "AI Agents",
  "Design Systems",
];

const Hero = () => {
  const scrollProgress = useRef(0);
  const sectionRef = useRef(null);

  useEffect(() => {
    const onScroll = () => {
      if (!sectionRef.current) return;
      const r = sectionRef.current.getBoundingClientRect();
      const total = r.height;
      const passed = Math.max(0, -r.top);
      scrollProgress.current = Math.max(0, Math.min(1, passed / total));
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const onCTA = () => {
    const el = document.querySelector("#manifesto");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      ref={sectionRef}
      data-testid="hero-section"
      className="relative w-full min-h-screen overflow-hidden"
      id="hero"
    >
      <div className="ambient" />
      <ParticleHero scrollProgressRef={scrollProgress} />

      <div className="relative z-10 min-h-screen flex flex-col justify-between px-6 md:px-12 lg:px-20 pt-28 md:pt-32 pb-8">
        <div className="flex items-center justify-between">
          <span className="text-[11px] tracking-[0.4em] uppercase text-muted-ink font-mono" data-testid="hero-eyebrow">
            DLV / 01
          </span>
          <span className="chip" data-testid="hero-status">
            <span className="chip-dot" />
            Studio open · taking projects
          </span>
        </div>

        <div className="max-w-5xl">
          <h1
            data-testid="hero-headline"
            className="font-display font-bold leading-[0.95] tracking-tighter text-[#0a0a0b]"
            style={{ fontSize: "clamp(2.6rem, 7.5vw, 7rem)" }}
          >
            We delve deep,
            <br />
            so you can <span className="accent-text">venture far.</span>
          </h1>
          <p
            data-testid="hero-subline"
            className="mt-8 max-w-2xl text-base md:text-lg text-muted-ink leading-relaxed"
          >
            Delventure is an AI-driven digital studio and consultancy that turns
            business complexity into clarity, momentum, and measurable growth.
          </p>

          <div className="mt-10 flex items-center gap-6">
            <MagneticButton testId="cta-start-descent" onClick={onCTA}>
              Start a project
              <ChevronDown size={16} strokeWidth={1.5} />
            </MagneticButton>
            <span className="hidden md:inline-flex items-center gap-2 text-[10px] tracking-[0.4em] uppercase text-muted-ink">
              <ArrowDown size={12} /> Scroll
            </span>
          </div>
        </div>

        <div className="space-y-6">
          <div className="overflow-hidden border-y border-[var(--hairline)] py-3">
            <div className="marquee gap-12 text-[10px] tracking-[0.4em] uppercase text-[#0a0a0b]">
              {[...TICKER_ITEMS, ...TICKER_ITEMS].map((t, i) => (
                <span key={i} className="flex items-center gap-12">
                  <span>{t}</span>
                  <span className="text-muted-ink">/</span>
                </span>
              ))}
            </div>
          </div>

          <div className="flex items-end justify-between">
            <div className="flex flex-col gap-1">
              <span className="text-[10px] tracking-[0.4em] uppercase text-muted-ink">Index</span>
              <span className="font-display text-2xl md:text-3xl text-[#0a0a0b]">00 — Hero</span>
            </div>
            <div className="hidden md:flex flex-col items-end gap-1 text-right">
              <span className="text-[10px] tracking-[0.4em] uppercase text-muted-ink">Studio</span>
              <span className="font-mono text-xs text-muted-ink">Remote · Worldwide · Always-on</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
